'use client';

import { usePathname } from 'next/navigation';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import React, { useContext, useState, useEffect } from 'react';
import { useSidebar } from '@/contexts/SidebarContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion } from 'framer-motion';
import Image from 'next/image';
import logo from '@/app/icon.png';
import config from '@/config';
import { UserContext } from '@/context/UserContext';
import ButtonSignin from '@/components/login/ButtonSignin';
import ButtonAccount from '@/components/login/ButtonAccount';
import SimplePricingModal from '@/components/modals/SimplePricingModal';
import LanguageSelector from '@/components/common/LanguageSelector';
import { ArrowRightFromLine, Clapperboard, Images, Sparkles } from 'lucide-react';
import { wanPageVersions, wanVersionHref, wanVersionName } from '@/lib/wan/pages';

// 导航项配置
const navigation = [
  {
    group: 'Wan Models',
    items: wanPageVersions.map(version => ({
      name: wanVersionName(version),
      href: wanVersionHref(version),
      icon: <Sparkles className="size-5" />,
    })),
  },
  {
    group: 'Video',
    items: [

      {
        name: 'Video with Audio',
        href: '/wan22',
        isHighlighted: false,
        isHot: false,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V7.5A2.25 2.25 0 014.5 5.25H12a2.25 2.25 0 012.25 2.25z"
            />
          </svg>
        ),
      },
      {
        name: 'Speech to Video',
        href: '/speech-to-video',
        isHighlighted: false,
        isHot: false,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
            />
          </svg>
        ),
      },
      {
        name: 'Image to Video',
        href: '/image-to-video',
        isHighlighted: false,
        isHot: false,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
            />
          </svg>
        ),
      },
      {
        name: 'Text to Video',
        href: '/text-to-video',
        isHighlighted: false,
        isHot: false,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V7.5A2.25 2.25 0 014.5 5.25H12a2.25 2.25 0 012.25 2.25z"
            />
          </svg>
        ),
      },
      {
        name: 'Video to Video',
        href: '/video-to-video',
        isHighlighted: false,
        isHot: false,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
            />
          </svg>
        ),
      },
      {
        name: 'Video Extend',
        href: '/video-extend',
        icon: <ArrowRightFromLine className="size-5" />,
      },
      {
        name: 'Reference to Video',
        href: '/reference-to-video',
        icon: <Clapperboard className="size-5" />,
      },
      {
        name: 'Wan2.2 Animate',
        href: '/wan22-animate',
        isHighlighted: false,
        isHot: true,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423L16.5 15.75l.394 1.183a2.25 2.25 0 001.423 1.423L19.5 18.75l-1.183.394a2.25 2.25 0 00-1.423 1.423z"
            />
          </svg>
        ),
      },
      
    ],
  },
  {
    group: 'Image',
    items: [
      {
        name: 'Image to Image',
        href: '/image-to-image',
        icon: <Images className="size-5" />,
      },
      {
        name: 'Text to Image',
        href: '/text-to-image',
        isHighlighted: false,
        isHot: false,
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
            />
          </svg>
        ),
      },
    ],
  },
  {
    group: 'Explore',
    items: [
      {
        name: 'Explore',
        href: '/explore',
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122"
            />
          </svg>
        ),
      },
    ],
  },
  {
    group: 'History',
    items: [
      {
        name: 'My Creations',
        href: '/history',
        icon: (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      },
    ],
  },
];

const groupVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  show: { opacity: 1, x: 0 },
};

/**
 * 侧边栏导航组件
 * 显示应用程序导航菜单和用户信息
 */
export function Sidebar() {
  const pathname = usePathname();
  const { isCollapsed } = useSidebar();
  const { user, fetchUserInfo } = useContext(UserContext);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // 🔥 LCP优化：客户端检测，延迟复杂效果
  useEffect(() => {
    setIsClient(true);
    
    // 延迟加载动画效果
    const timer = setTimeout(() => {
      const particles = document.querySelectorAll('.absolute.top-1\\/4.left-2, .absolute.top-3\\/4.left-4, .absolute.top-1\\/2.left-1');
      particles.forEach((particle, index) => {
        particle.classList.add('animate-pulse');
        if (index === 1) particle.classList.add('animate-ping', 'delay-1000');
        if (index === 2) particle.classList.add('animate-bounce', 'delay-500');
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  /**
   * 处理升级按钮点击事件
   */
  const handleUpgradeClick = () => {
    setShowPricingModal(true);
  };

  // 检查用户是否为付费会员
  const isPremiumUser = user?.has_access || false;

  return (
    <motion.div
      className={cn(
        "flex h-full flex-col relative",
        "bg-black/95 backdrop-blur-xl",
        "shadow-[0_0_50px_rgba(16,185,129,0.1)]",
        "w-full"
      )}
      initial={false}
      transition={{
        duration: 0.3,
        ease: "easeInOut"
      }}
      style={{
        background: `linear-gradient(180deg, 
          rgba(0,0,0,0.98) 0%, 
          rgba(6,20,14,0.95) 40%, 
          rgba(0,0,0,0.98) 100%)`,
      }}
    >
      {/* Cinematic edge glow */}
      <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-emerald-400/40 to-transparent" />
      
      {/* 🔥 LCP优化：延迟环境光粒子效果 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* 延迟加载动画效果 */}
        <div className="absolute top-1/4 left-2 w-1 h-1 bg-emerald-400/60 rounded-full" />
        <div className="absolute top-3/4 left-4 w-0.5 h-0.5 bg-green-300/40 rounded-full" />
        <div className="absolute top-1/2 left-1 w-1.5 h-1.5 bg-emerald-500/30 rounded-full" />
      </div>
      {/* Header Section */}
      <div className="px-4 py-6 relative z-10">
        <Link 
          href="/" 
          className="group flex items-center gap-3 transition-all duration-500 hover:opacity-90 relative"
        >
          <div className="relative">
            {/* 🔥 LCP优化：简化Logo效果 */}
            <Image
              src={logo}
              alt=""
              className="rounded-lg ring-1 ring-emerald-500/30 shadow-lg h-9 w-9"
              width={36}
              height={36}
              priority
            />
          </div>
          <span className="text-lg font-bold text-white">
            {config.appName}
          </span>
        </Link>
      </div>

      {/* Navigation Section */}
      <ScrollArea className="flex-1 px-3 relative z-10">
        <nav className="space-y-6 pb-4">
          {navigation.map((group) => (
            <motion.div
              key={group.group}
              variants={groupVariants}
              initial="hidden"
              animate="show"
              className="space-y-1"
            >
              {/* Group Label */}
              {!isCollapsed && (
                <div 
                  className="px-3 py-2 relative"
                  style={{
                    // 🔥 LCP优化：使用 content-visibility 优化渲染
                    contentVisibility: 'auto',
                    containIntrinsicSize: '0 32px'
                  }}
                >
                  <p 
                    style={{
                      // 🔥 LCP优化：内联样式，避免复杂的 Tailwind 类
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: 'rgba(52, 211, 153, 0.9)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      userSelect: 'none',
                      position: 'relative',
                      zIndex: 10,
                      fontDisplay: 'swap',
                      fontFeatureSettings: '"kern" 1',
                      textRendering: 'optimizeLegibility',
                      // 延迟加载复杂效果
                      ...(isClient && {
                        filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.6))'
                      })
                    }}
                  >
                    <span>{group.group}</span>
                  </p>
                  {/* Section divider line */}
                  <div className="absolute bottom-0 left-3 right-3 h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
                </div>
              )}

              {/* Navigation Items */}
              <div className="space-y-1">
                {group.items.map((item) => {
                  // 修复国际化路由匹配：移除语言前缀后再进行匹配
                  const cleanPathname = pathname.replace(/^\/[a-z]{2}(\/|$)/, '/').replace(/^\/$/, '');
                  const cleanHref = item.href;
                  const isActive = cleanPathname === cleanHref || 
                    (cleanHref !== '/' && cleanPathname.startsWith(cleanHref + '/'));
                  
                  return (
                    <motion.div key={item.name} variants={itemVariants}>
                      <Link
                        href={item.href}
                        className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2.5",
                          "text-sm font-medium transition-colors duration-300",
                          "focus:outline-none focus:ring-2 focus:ring-emerald-500/60",
                          isCollapsed && "justify-center px-2",
                          isActive 
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            : "text-gray-300 hover:text-white hover:bg-gray-700/50 border border-transparent",
                          "backdrop-blur-sm"
                        )}
                      >
                        {/* 🔥 LCP优化：简化活跃指示器 */}
                        {isActive && (
                          <div className="absolute left-0 top-0 h-full w-1 bg-emerald-400" />
                        )}
                        
                        {/* Icon */}
                        <div className={cn(
                          "flex-shrink-0",
                          isActive 
                            ? "text-emerald-400" 
                            : "text-gray-400 hover:text-emerald-300"
                        )}>
                          {item.icon}
                        </div>

                        {/* Label */}
                        {!isCollapsed && (
                          <span className="truncate flex-1">
                            <span>{item.name}</span>
                          </span>
                        )}

                        {/* 🔥 LCP优化：简化Hot标签 */}
                        {!isCollapsed && item.isHot && (
                          <span className={cn(
                            "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold",
                            "bg-red-600/30 text-red-300 border border-red-500/50"
                          )}>
                            <span>HOT</span>
                          </span>
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </nav>
      </ScrollArea>

             {/* Footer Section */}
       <div className={cn(
         "border-t border-gray-600/30 p-4 space-y-4 relative z-10",
         "bg-black/60",
         isCollapsed && "p-3 space-y-3"
       )}>
         {/* 🔥 LCP优化：简化顶部边框 */}
         <div className="absolute top-0 left-4 right-4 h-px bg-gray-500/30" />
         
         {/* Discord & Language Row */}
         <div className={cn(
           "flex gap-2 relative z-10",
           isCollapsed ? "flex-col space-y-2" : "items-center justify-between"
         )}>
           {/* 🔥 LCP优化：简化Discord按钮
           <a
             href="https://discord.gg/kd5KQYznq6"
             target="_blank"
             rel="noopener noreferrer"
             className={cn(
               "inline-flex items-center gap-2 rounded-lg px-3 py-2",
               "text-sm font-medium text-gray-300 transition-colors duration-300",
               "bg-gray-800/80 border border-gray-600/30",
               "hover:bg-gray-700/80 hover:text-white hover:border-gray-500/60",
               "focus:outline-none focus:ring-2 focus:ring-gray-500/60",
               "backdrop-blur-sm",
               isCollapsed ? "w-full justify-center" : "flex-shrink-0"
             )}
             title="Join Discord Community"
           >
             <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
               <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
             </svg>
             {!isCollapsed && <span>Discord</span>}
           </a> */}

           {/* Language Selector */}
           <div className={cn(isCollapsed ? "w-full" : "flex-1 max-w-[120px]")}>
             <LanguageSelector 
               variant="sidebar"
               showLabel={!isCollapsed}
               className={cn(
                 "bg-gray-800/80 border-gray-600/30",
                 "hover:bg-gray-700/80 hover:border-gray-500/60",
                 "text-gray-300 hover:text-gray-200 transition-colors duration-300",
                 "backdrop-blur-sm",
                 isCollapsed ? "w-full h-10 justify-center" : "w-full h-10"
               )}
             />
           </div>
         </div>

         {/* 🔥 LCP优化：简化升级按钮，延迟复杂效果 */}
         {user && !isPremiumUser && (
           <Button
             onClick={handleUpgradeClick}
             className={cn(
               "w-full h-10 font-bold relative",
               "bg-emerald-700 hover:bg-emerald-600",
               "text-white border border-emerald-500/60",
               "transition-colors duration-300",
               "focus:ring-2 focus:ring-emerald-500/60",
               "backdrop-blur-sm",
               "shadow-sm",
               isCollapsed && "px-0"
             )}
             onMouseEnter={(e) => {
               // 🔥 关键优化：延迟复杂效果加载
               setTimeout(() => {
                 e.currentTarget.classList.add('group');
                 e.currentTarget.classList.add('overflow-hidden');
                 e.currentTarget.classList.add('transform');
                 e.currentTarget.classList.add('hover:scale-105');
                 e.currentTarget.classList.add('shadow-[0_0_20px_rgba(16,185,129,0.4)]');
                 e.currentTarget.classList.add('hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]');
               }, 1000);
             }}
           >
             <span className="flex items-center justify-center gap-2">
               {isCollapsed ? (
                 <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                 </svg>
               ) : (
                 <>
                   <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                   </svg>
                   <span><span>UPGRADE NOW</span></span>
                 </>
               )}
             </span>
           </Button>
         )}

        {/* User Section */}
        <div className={cn(
          "space-y-3 relative z-10",
          isCollapsed && "space-y-2"
        )}>
          {user ? (
            <div className={cn(
              "flex items-center gap-3",
              isCollapsed && "flex-col gap-2"
            )}>
              {/* User Account */}
              <div className={cn("flex-1", isCollapsed && "w-full flex justify-center")}>
                <ButtonAccount
                  supabaseUser={user}
                  mobileStyle={isCollapsed}
                  avatarOnly={isCollapsed}
                  fullWidth={!isCollapsed}
                  className={cn(
                    "bg-gray-800/80 border-gray-600/30",
                    "hover:bg-gray-700/80 hover:border-gray-500/60",
                    "text-gray-300 hover:text-gray-200 transition-colors duration-300",
                    "backdrop-blur-sm",
                    isCollapsed ? "w-10 h-10" : "h-10"
                  )}
                />
              </div>

              {/* 🔥 LCP优化：简化积分显示 */}
              <div className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg h-10",
                "bg-gray-800/80 border border-gray-600/30",
                "text-sm font-medium transition-colors duration-300",
                "hover:bg-gray-700/80 hover:border-gray-500/60",
                "text-gray-300 hover:text-gray-200",
                "backdrop-blur-sm",
                isCollapsed ? "w-full justify-center px-2" : "min-w-[80px]"
              )}>
                <svg className="h-4 w-4 text-emerald-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="currentColor"/>
                  <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.6"/>
                  <path d="M9 10h6M9 14h6M12 8v2M12 14v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.8"/>
                </svg>
                {!isCollapsed && (
                  <span className="text-emerald-400 font-bold">
                    <span>{user.left_credits || 0}</span>
                  </span>
                )}
              </div>
            </div>
          ) : (
            <ButtonSignin 
              className={cn(
                "w-full h-10 font-bold",
                "bg-emerald-700 hover:bg-emerald-600",
                "text-white border border-emerald-500/60",
                "transition-colors duration-300",
                "backdrop-blur-sm",
                "shadow-sm",
                isCollapsed && "px-0"
              )}
              avatarOnly={isCollapsed}
            />
          )}
        </div>
      </div>

      {/* Pricing Modal */}
      <SimplePricingModal 
        isOpen={showPricingModal} 
        onClose={() => setShowPricingModal(false)} 
      />
    </motion.div>
  );
}

// 🔥 LCP优化：使用React.memo避免不必要的重渲染
export default React.memo(Sidebar);
